import Api from 'api';
import { ReactComponent as AutomationDoneIcon } from 'assets/icons/automation-done.svg';
import { ReactComponent as AutomationErrorIcon } from 'assets/icons/automation-error.svg';
import { ReactComponent as AutomationIdleIcon } from 'assets/icons/automation-idle.svg';
import { ReactComponent as AutomationWarningIcon } from 'assets/icons/automation-warn.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus-square.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg';
import Button from 'atoms/Button';
import Container from 'atoms/Container';
import ContentBox from 'atoms/ContentBox';
import ListHeader from 'atoms/ListHeader/ListHeader';
import Loader from 'layout/molecules/Loader';
import { useEffect, useState } from 'react';

type AutomationStatus = {
    id: string;
    name: string;
    status: 'IDLE' | 'ACTIVE' | 'WARNING' | 'DONE' | 'ERROR';
};

export const AutomationPage = () => {
    const [automations, setAutomations] = useState<AutomationStatus[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        Api.defaultApi()
            .fetchAutomationStatus()
            .then((response) => {
                setAutomations(response.automations as AutomationStatus[]);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, []);

    const currentMonth = new Date().toLocaleString('default', {
        month: 'long',
    });
    return (
        <>
            <Container size="s">
                <ListHeader text="Automation">
                    {automations.length > 0 && (
                        <Button
                            size="m"
                            theme="blank"
                            shape="square"
                            to="/automation/configure"
                        >
                            <SettingsIcon />
                        </Button>
                    )}
                </ListHeader>
            </Container>
            <Container size="s">
                {isLoading ? (
                    <ContentBox>
                        <Loader />
                    </ContentBox>
                ) : automations.length === 0 ? (
                    <ContentBox>
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    background:
                                        'linear-gradient(135deg, #9B59B6, #8E44AD)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '24px',
                                    fontSize: '32px',
                                }}
                            >
                                ⚡
                            </div>
                            <h3
                                style={{
                                    margin: '0 0 12px 0',
                                    fontSize: '20px',
                                    fontWeight: '600',
                                    color: 'var(--text)',
                                }}
                            >
                                Automate your workflow
                            </h3>
                            <p
                                style={{
                                    margin: '0 0 32px 0',
                                    fontSize: '16px',
                                    lineHeight: '1.5',
                                    color: 'var(--text)',
                                    opacity: 0.7,
                                    maxWidth: '400px',
                                }}
                            >
                                Set up automations to handle recurring tasks
                                automatically. Save time by scheduling actions
                                that run on a regular basis.
                            </p>
                            <Button
                                size="m"
                                theme="primary"
                                to="/automation/configure/create"
                                style={{
                                    fontSize: '16px',
                                    padding: '12px 24px',
                                }}
                            >
                                <PlusIcon />{' '}
                                <span>Create your first automation</span>
                            </Button>
                        </div>
                    </ContentBox>
                ) : (
                    <ContentBox>
                        <ListHeader
                            text={`Month of ${currentMonth}`}
                        ></ListHeader>
                        {automations
                            .sort((a, b) => a.status.localeCompare(b.status))
                            .map((automation, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        margin: '12px 0',
                                        gap: '12px',
                                        width: '100%',
                                    }}
                                >
                                    <div style={{ width: '24px' }}>
                                        {automation.status === 'DONE' && (
                                            <AutomationDoneIcon
                                                style={{
                                                    maxHeight: '24px',
                                                }}
                                            />
                                        )}
                                        {automation.status === 'IDLE' && (
                                            <AutomationIdleIcon
                                                style={{
                                                    maxHeight: '24px',
                                                }}
                                            />
                                        )}
                                        {automation.status === 'ERROR' && (
                                            <AutomationErrorIcon
                                                style={{
                                                    maxHeight: '24px',
                                                }}
                                            />
                                        )}
                                        {automation.status === 'ACTIVE' && (
                                            <AutomationWarningIcon
                                                style={{
                                                    maxHeight: '24px',
                                                }}
                                            />
                                        )}
                                        {automation.status === 'WARNING' && (
                                            <AutomationWarningIcon
                                                style={{
                                                    maxHeight: '24px',
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        {automation.name}
                                    </div>
                                </div>
                            ))}
                    </ContentBox>
                )}
            </Container>
        </>
    );
};

export default AutomationPage;
